<script setup lang="ts">
  import { ref, computed, watch, nextTick, watchEffect, onBeforeMount } from 'vue';
  import { useConfigStore } from '@/store';
  import { parsePhoneNumberFromString, AsYouType, CountryCode } from 'libphonenumber-js';
  import CustomSearchSelectInput from '../CustomSearchSelectInput.vue';
  import type { Option } from '@/models/interfaces';
  import type { Country } from '@/models/types';
  import countries from '@/countries.json';
  import { hasFlag } from 'country-flag-icons';

  type PhoneOption = Option<Country>;

  const configStore = useConfigStore();

  defineProps({
    index: {
      type: String,
      required: true,
    },
  });

  /*
   * Validity can be check from outside
   * using @input and the exposed checkValidity method.
   */
  const emit = defineEmits<{
    (e: 'input'): void;
    (e: 'selectedPhoneCode', newCode: Country): void;
  }>();

  /*
   * Phone numbers are stored using the E.164 format
   * - 'displayedValue' is used to display a number formatted in a pleasant way to te user.
   * - 'shadowValue' is the Recommendation ITU-T E.164 equivalent
   *   using libphonenumber-js's parsePhoneNumberFromString method.
   */
  const shadowValue = defineModel<string>('value');
  const displayedValue = ref<string>('');

  const telRef = ref<HTMLInputElement>();

  const required = defineModel<boolean>('required');
  const disabled = defineModel<boolean>('disabled');

  const selectedOption = ref<PhoneOption>({
    value: {
      iso3: 'FRA',
      iso2: 'FR',
      phoneCode: '+33',
      name: 'France',
    },
    label: `FRA (+33)`,
    disabled: false,
  });

  // shadowValue validity
  const isShadowValid = ref<boolean>();
  // input validity (style) : Considered valid if isShadowValid or (!required && empty).
  const isInputValid = computed(() => {
    if (isShadowValid.value) return true;
    if (!required.value && !displayedValue.value) return true;
    return false;
  });

  const options = computed<Array<PhoneOption>>(() => {
    return getPrefixes().map((country) => {
      return {
        value: country,
        label: `${country.iso3} (${country.phoneCode})`,
        disabled: false,
      };
    });
  });

  /**
   * Initialises The country calling code if a value is provided.
   */
  onBeforeMount(() => {
    if (shadowValue.value) {
      const phoneNumber = parsePhoneNumberFromString(shadowValue.value);
      if (phoneNumber) updateCountryCode(phoneNumber.country);
    }
  });

  watch(() => required.value, handleInput);

  watch(
    () => selectedOption.value,
    () => {
      emit('selectedPhoneCode', selectedOption.value.value);
      handleInput();
    },
  );

  // Initialization Logic
  const initialized = ref(false);
  const unwatchFirstAssertion = watch(
    () => shadowValue.value,
    (newValue, oldValue) => {
      if (newValue && !oldValue) {
        const phoneNumber = parsePhoneNumberFromString(newValue);
        displayedValue.value = phoneNumber ? phoneNumber.format('NATIONAL') : newValue;
        initialized.value = true;
      }
      nextTick(() => {
        handleInput();
      });
    },
    { immediate: true },
  );

  watchEffect(() => {
    if (initialized.value) {
      unwatchFirstAssertion();
    }
  });

  async function handleInput() {
    if (!telRef.value) return;

    if (!displayedValue.value) {
      isShadowValid.value = false;
      shadowValue.value = '';
    } else {
      displayedValue.value = new AsYouType({
        defaultCountry: selectedOption.value.value.iso2 as CountryCode,
      }).input(displayedValue.value);

      await nextTick();

      validate(displayedValue.value);
    }

    emit('input');
  }

  function validate(raw: string) {
    let validationResult = false;

    try {
      const phoneNumber = parsePhoneNumberFromString(raw, {
        defaultCountry: selectedOption.value.value.iso2 as CountryCode,
      });
      if (phoneNumber) {
        shadowValue.value = phoneNumber.format('E.164');
        displayedValue.value = phoneNumber.format('NATIONAL');
        updateCountryCode(phoneNumber.country);
        validationResult = phoneNumber.isValid();
      } else {
        shadowValue.value = displayedValue.value;
      }
    } catch (err) {
      console.warn(err);
    }

    isShadowValid.value = validationResult;
  }

  function checkValidity() {
    return isShadowValid.value || false;
  }

  function getPrefixes(): Array<Country> {
    const { main_countries } = configStore.config;

    return countries.sort((a, b) => {
      const aIsMain = main_countries?.includes(a.iso3);
      const bIsMain = main_countries?.includes(b.iso3);

      if (aIsMain && bIsMain) return a.iso3.localeCompare(b.iso3);
      if (aIsMain) return -1;
      if (bIsMain) return 1;
      return a.iso3.localeCompare(b.iso3);
    });
  }

  function updateCountryCode(iso2: string | undefined) {
    if (!iso2) return;
    const prefixFound = getPrefixes().find((prefix) => prefix.iso2 === iso2);
    if (prefixFound) {
      const optionFound = options.value.find((option) => option.value.iso3 === prefixFound.iso3);
      if (optionFound) selectedOption.value = optionFound;
    }
  }

  function getFlagUrl(iso2: string): string {
    if (iso2 && hasFlag(iso2)) {
      return `http://purecatamphetamine.github.io/country-flag-icons/1x1/${iso2}.svg`;
    } else {
      return '';
    }
  }

  defineExpose({ checkValidity });
</script>

<template>
  <div
    class="custom-phone-field flex flex-nowrap items-center mb-2 grow bg-grey-lightest px-2 rounded-md"
  >
    <img
      class="w-6 h-6 bg-grey-lightest rounded-full mr-2"
      :src="`${getFlagUrl(selectedOption.value.iso2)}`"
    />

    <custom-search-select-input
      v-model:options="options"
      v-model:selected-option="selectedOption"
      v-model:required="required"
      v-model:disabled="disabled"
      :index="index"
      :standalone="false"
      :is-filtered-on-search="true"
      class="w-[10rem]"
    />

    <div class="border-l h-6 border-grey-light" />
    <input
      :id="`${index}`"
      ref="telRef"
      v-model="displayedValue"
      :name="`${index}`"
      type="tel"
      :placeholder="$t('client_info.placeholder.phone')"
      :required="required"
      :disabled="disabled"
      class="bg-grey-lightest p-4 py-2 rounded-md grow"
      :class="[{ valid: isInputValid }, { invalid: !isInputValid }]"
      @input="handleInput"
    />
  </div>
</template>

<style scoped lang="css">
  .custom-phone-field:has(input.invalid) {
    border: 2px solid var(--red-error);
  }
  .custom-phone-field:has(input:placeholder-shown) {
    border: none;
  }
  .custom-phone-field:has(input:focus-within),
  .custom-phone-field:has(select:focus-within) {
    border: none;
  }
  .custom-phone-field:has(input[required]) {
    border-left: 6px solid var(--red-error);
  }
  .custom-phone-field:has(input.valid) {
    border-left: 6px solid var(--green-valid);
  }
</style>
